
import { ManualPart } from '../types';

export const part2: ManualPart = {
  id: "part-config",
  title: "CONFIGURACIÓN DE PROYECTOS",
  sections: [
    {
      id: "env-intro",
      title: "6. ENTORNO Y ATMÓSFERA",
      content: [
        "Esta sección es el fundamento físico de LiveSync Pro. Define las condiciones climáticas y espaciales que alteran la velocidad de propagación, la absorción de frecuencias y la coherencia de fase en tiempo real.",
        "**6.1 Condiciones Básicas (Tab BASIC)**",
        "**A. Temperatura Ambiente**",
        "• **Rango:** -10°C a 50°C. **Unidad:** Celsius (°C).",
        "• **Para qué sirve:** Determina la velocidad del sonido (c). El aire caliente acelera las ondas sonoras.",
        "CODE:c ≈ 331.3 + (0.6 × T)",
        "• **Ejemplo práctico:**",
        "  • Soundcheck a las 2pm: 28°C (c ≈ 348 m/s).",
        "  • Show a las 9pm: 18°C (c ≈ 342 m/s).",
        "  • Diferencia: ~6 m/s. En una torre a 100m, esto genera un **drift de ~1.7ms**, suficiente para crear un eco audible.",
        "• **Cómo usar:** Mide la temperatura actual en el FOH (siempre a la sombra).",
        "",
        "**B. Humedad Relativa**",
        "• **Rango:** 0% a 100%.",
        "• **Para qué sirve:** Afecta la absorción de altas frecuencias (HF).",
        "• **Regla técnica:** El aire seco (< 30%) genera una pérdida masiva de HF (>1dB cada 30m @ 10kHz).",
        "• **Reglas de oro:**",
        "  • **Indoor controlado:** 40-60% (valor típico).",
        "  • **Outdoor verano:** 60-90% (el sonido viaja mejor).",
        "  • **Outdoor invierno/montaña:** 10-30% (requiere ecualización High Shelf agresiva en torres).",
        "",
        "**C. Altitud**",
        "• **Rango:** 0m a 5000m sobre el nivel del mar. **Unidad:** Metros o Pies.",
        "• **Para qué sirve:** Afecta la presión atmosférica y la densidad del aire (ISO 9613-1).",
        "  • Nivel del mar: 101.325 kPa (Presión estándar).",
        "  • 2000m (CDMX): 79 kPa (Menor densidad = menor resistencia).",
        "• **Ciudades de referencia:** 0m (Miami/Río), 500m (Bogotá Aeropuerto), 1500m (Denver), 2600m (Bogotá Ciudad), 3650m (La Paz).",
        "",
        "**D. Viento**",
        "• **Velocidad:** 0 a 30 m/s (0 a 108 km/h).",
        "• **Dirección:** 0° a 360°.",
        "  • **0° = Tailwind:** El viento empuja el sonido hacia el público.",
        "  • **180° = Headwind:** El viento sopla contra el PA.",
        "  • **90°/270° = Crosswind:** Viento lateral (el más peligroso para la fase).",
        "• **Warnings de Seguridad:**",
        "  • 🟡 **10-15 m/s (36-54 km/h):** PELIGRO - Límite de operación estructural.",
        "  • 🔴 **> 15 m/s (> 54 km/h):** CRÍTICO - BAJAR SISTEMA INMEDIATAMENTE.",
        "• **Efectos acústicos:**",
        "  • **Headwind:** Refracción ascendente (crea zonas de sombra acústica).",
        "  • **Tailwind:** Refracción descendente (el sonido llega más lejos).",
        "  • **Crosswind:** Inestabilidad de fase en HF (imposible alinear correctamente).",
        "• **Cómo medir:** Usa apps (Windy), anemómetros portátiles o la observación de banderas (horizontales ≈ 10 m/s)."
      ]
    },
    {
      id: "env-advanced",
      title: "6.2 Condiciones Avanzadas (Tab ADVANCED)",
      content: [
        "**E. Ocupación del Venue (%)**",
        "• **Rango:** 0% a 100%.",
        "• **Función:** Corrige temperatura y humedad por el calor metabólico de la audiencia.",
        "• **Efectos:**",
        "  1. **Aumento de Temp:** Outdoor (+1°C por cada 20% ocupación), Indoor (+5°C con venue lleno).",
        "  2. **Aumento de Humedad:** Outdoor (+5% humedad), Indoor (+20% con venue lleno).",
        "  3. **Absorción Adicional:** Los cuerpos humanos absorben HF (4-8kHz) y bloquean el paso de graves cerca del suelo.",
        "",
        "**F. Drift Térmico (Predicción)**",
        "• **Propósito:** Predecir cuánto cambiarán los delays sin tener que volver a medir en campo.",
        "• **Proceso:** Configura el sistema a la hora del Soundcheck e ingresa la temperatura esperada del Show. LiveSync calculará el nuevo delay compensado."
      ]
    },
    {
      id: "env-venue",
      title: "6.3 Configuración de Venue (Tab VENUE)",
      content: [
        "**G. Tipo de Venue**",
        "TABLE:Aspecto | Indoor (Cerrado) | Outdoor (Abierto)",
        "Reverberación | RT60 Calculado / Medido | RT60 = 0 (Campo Libre)",
        "Viento | Ignorado | Crítico (Refracción)",
        "Ground Effect | Menor impacto | Mayor impacto",
        "Reflexiones | Image Source Method | Solo reflexión del piso",
        "",
        "**H. Layout del Escenario**",
        "1. **Proscenium (Estándar Frontal) ✅:** Público frente al escenario. PA L/R o Mono Central.",
        "2. **Arena 360° (N/S/E/W):** Escenario central, público 360°. Requiere 4 arreglos PA y multiplica logística x4.",
        "3. **Teatro Circular / In-the-Round:** Escenario central redondo con cluster central o 4 caras.",
        "4. **Thrust (Pasarela en T):** Escenario con extensión hacia el público. Requiere Side Fills dedicados.",
        "5. **Traverse (Pasarela Central):** Público enfrentado a los costados del escenario."
      ]
    },
    {
      id: "env-acoustics",
      title: "6.4 Acústica de Sala (Solo Indoor)",
      content: [
        "**I. Dimensiones de la Sala**",
        "• **Largo, Ancho, Alto:** En metros. LiveSync calcula el volumen (V = L × W × H) e identifica **Modos Propios** (resonancias) entre 20-200Hz.",
        "",
        "**J. Material de Paredes**",
        "• **Concreto:** α = 0.02 (muy reflectivo).",
        "• **Ladrillo:** α = 0.03.",
        "• **Drywall / Pladur:** α = 0.05.",
        "• **Madera:** α = 0.10.",
        "• **Cortinaje Pesado:** α = 0.40 (más absorbente).",
        "",
        "**K. Material de Techo**",
        "• **Concreto:** Muy reflectivo. **Cielo Acústico:** Absorbente. **Metal:** Reflectivo. **Madera:** Moderadamente absorbente.",
        "",
        "**L. RT60 Medido en Campo (Checkbox)**",
        "Si dispones de una medición real del tiempo de reverberación, puedes anular el cálculo teórico del software.",
        "**Cómo activar:**",
        "1. Marca el checkbox **'Usar RT60 Medido en Campo'**.",
        "2. Ingresa el valor obtenido en segundos (ej: 1.45s) correspondiente a la banda de **1kHz**.",
        "3. LiveSync ignorará los materiales de paredes/techo y usará este valor real para los cálculos de inteligibilidad (STI).",
        "**Métodos de medición válidos:**",
        "• **SMAART/REW:** Análisis de Respuesta al Impulso para leer RT60 @ 1kHz.",
        "• **Método del Globo:** Explotar un globo de 30cm a 2-3m de altura y analizar el decaimiento de -60dB.",
        "• **Clap/Pistola:** Uso de aplauso fuerte o pistola de fogueo para medir caída de 60dB.",
        "• **Ubicación:** Realiza la medición siempre en la **Posición de FOH** para resultados representativos."
      ]
    },
    {
      id: "pa-basic-config",
      title: "7. SISTEMA PRINCIPAL (PA)",
      content: [
        "Esta sección configura el arreglo principal de altavoces (Line Array o Point Source). Es el motor primario de cobertura del evento.",
        "**7.1 Configuración Básica**",
        "**A. Configuración L/R**",
        "• **Stereo (L/R Arrays):** Dos arreglos, izquierda y derecha. ✅ **Estándar**.",
        "• **Mono / Center Array:** Un solo arreglo central (pequeños venues, speech).",
        "",
        "**B. Modelo Main (Top)**",
        "Selecciona el modelo de altavoz del arreglo principal.",
        "**Catálogo incluido (200+ modelos):**",
        "• **L-Acoustics:** K1, K2, K3, Kara II, Kiva II, A15, A10, ARCS",
        "• **d&b audiotechnik:** GSL8, KSL8, J8, V8, Y8, T10, E8",
        "• **Meyer Sound:** Panther, LEO-M, Lyon-M, Leopard, LINA",
        "• **JBL Professional:** VTX A12, V25, A8",
        "• **Adamson:** E15, E12, S10, CS10",
        "• **RCF, Electro-Voice, FBT, BassBoss, Yamaha, y más...**",
        "",
        "**Datos incluidos por modelo:**",
        "• SPL máximo @ 1m",
        "• Dispersión nominal (H × V)",
        "• Sensibilidad (dB)",
        "• Peso por caja",
        "",
        "**C. Cajas Main por Lado**",
        "• **Rango:** 1 a 24 cajas.",
        "• **Estándar:** 8-12 cajas (teatros/arenas).",
        "• **Festivales:** 14-18 cajas (tiro largo).",
        "🔴 **Warning automático:** Si > 16 cajas, LiveSync lo indica en rojo.",
        "",
        "**D. Altura del Array (m)**",
        "• Altura desde el suelo hasta el centro acústico del arreglo.",
        "• **Típico:** 8-12m (hangs estándar).",
        "• **Festivales:** 12-18m (tiro largo, evitar obstáculos).",
        "**Afecta:**",
        "• Cálculo de ground effect (comb filtering).",
        "• Ángulo de disparo vertical.",
        "• Off-axis penalty (Olson).",
        "",
        "**E. Ancho del Escenario (m)**",
        "• Distancia entre los dos arreglos L/R.",
        "• **Recomendado:** 12-20m.",
        "• **Fórmula FOH óptima:** FOH Distance ≈ 1.5 × Stage Width (triángulo equilátero)."
      ]
    },
    {
      id: "sub-array-designer",
      title: "7.2 Subwoofer Array Designer",
      content: [
        "**F. Modelo de Subwoofer**",
        "Catálogo de subs disponibles: L-Acoustics (KS28, SB28), Meyer (1100-LFC, 900-LFC), d&b (SL-SUB, J-SUB), JBL (VTX B28), Adamson (E219).",
        "",
        "**G. Topología / Modo**",
        "Configura cómo están dispuestos físicamente los subs.",
        "**1. Omnidireccional (Estándar)**",
        "• Radiación 360° (igual energía al frente y atrás).",
        "• Máxima eficiencia al frente.",
        "• **Problema:** Mucho spill hacia el escenario (micrófonos acoplan).",
        "• **Uso:** Eventos sin micrófonos sensibles, DJs.",
        "",
        "**2. Cardioide Invertido (CSA)**",
        "• **Config:** 2 frente + 1 atrás invertido (ratio 2:1).",
        "• Cancelación de fase activa en la parte trasera.",
        "• **Rechazo trasero:** ~15dB.",
        "• **Pérdida al frente:** -3dB vs omni.",
        "• **Uso:** Orquestas, pop, corporate (micrófonos en escenario).",
        "",
        "**3. End-Fire Line**",
        "• Fila india con delays progresivos.",
        "• Máximo 'tiro' (throw) y rechazo trasero.",
        "• **Rechazo trasero:** ~12-20dB.",
        "• **Requiere:** Mucho espacio físico (profundidad).",
        "• **Uso:** EDM/Festivals con necesidad de impacto a larga distancia.",
        "",
        "**H. Cantidad Total Subs**",
        "• Número total de cajas de subwoofer.",
        "• **Típico:** 8-16 subs (small/medium venues).",
        "• **Festivales:** 24-48 subs.",
        "**LiveSync calcula automáticamente:**",
        "• SPL máximo teórico (suma coherente).",
        "• Spacing requerido para configuración cardioide.",
        "• Delays para end-fire."
      ]
    },
    {
      id: "fills-and-splay",
      title: "7.3 Frontfills & Outfills",
      content: [
        "Sistemas de cobertura complementaria.",
        "**I. Frontfills (Checkbox Enable/Disable)**",
        "• Pequeños altavoces en el lip del escenario. Cubren las primeras filas (0-5m).",
        "• **Parámetros:** Modelo (catálogo), Cantidad, Operación (m).",
        "• **Cuándo usar:** Escenarios altos (> 1.5m), primeras filas sin cobertura directa.",
        "",
        "**J. Outfills / Side PA (Checkbox Enable/Disable)**",
        "• Arreglos laterales para cobertura exterior.",
        "• Cubren zonas fuera del ángulo horizontal del main PA.",
        "• **Parámetros:** Modelo (catálogo), Cajas por Lado (L + R).",
        "• **Cuándo usar:** Venues muy anchos (> 30m), audiencia extendida lateralmente, Thrust stages."
      ],
      subsections: [
        {
          title: "7.4 Ángulos de Separación (Splay Angles)",
          content: [
            "Define la curvatura del Line Array.",
            "**K. Editor de Splay**",
            "• Campo de texto CSV separado por comas.",
            "• **Formato:** 0, 0.5, 1, 2, 3, 4, 5...",
            "• **Unidad:** Grados (°).",
            "**Reglas importantes:**",
            "• El número de ángulos debe ser **1 menos** que el número de cajas (Ejemplo: 10 cajas → 9 ángulos).",
            "• 🔴 **Warning:** Si hay mismatch, LiveSync lo indica en rojo.",
            "**Interpretación:** Ángulo 1 (Separación entre Caja 1 y 2), Ángulo 2 (Caja 2 y 3), etc.",
            "",
            "**L. Presets Rápidos**",
            "LiveSync ofrece 3 presets automáticos:",
            "1. **0° (Tiro Largo):** Todos los ángulos = 0°. Array completamente recto. **Uso:** Throw extremo (> 80m), festivales.",
            "2. **J-Shape:** Progresión logarítmica. Ángulos pequeños arriba (0-1°), grandes abajo (4-7°). **Uso:** Estándar profesional, cobertura uniforme.",
            "3. **Arco Constante:** Todos los ángulos iguales (2-3°). **Uso:** Cobertura corta uniforme.",
            "**Cómo aplicar:** Haz clic en el botón del preset. LiveSync genera automáticamente los ángulos que puedes editar manualmente después."
          ]
        }
      ]
    },
    {
      id: "stage-world-monitors",
      title: "8. MONITORES Y ESCENARIO (STAGE WORLD)",
      content: [
        "Configura los sistemas de monitoreo para músicos.",
        "**8.1 Pisos (Wedges) (Checkbox)**",
        "**A. Modelo**",
        "• Catálogo de monitores profesionales:",
        "  • **L-Acoustics:** X15 HiQ, X12, X8",
        "  • **d&b:** M2, M4, MAX2",
        "  • **Meyer:** MJF-212A, MJF-210",
        "  • **JBL:** VTX M22, M20",
        "",
        "**B. Cantidad Total**",
        "• Número de wedges en todo el escenario.",
        "• **Típico:** 6-10 wedges (banda 5-7 personas).",
        "",
        "**C. Mezclas**",
        "• Número de mezclas independientes (mixes).",
        "• **Estándar:** 6-8 mixes (1-2 por músico).",
        "• **Grandes shows:** 12-16 mixes.",
        "",
        "**8.2 Sidefills (Checkbox)**",
        "**D. Modelo**",
        "• Altavoces potentes en los laterales del escenario.",
        "• Cobertura general para toda la banda.",
        "",
        "**E. Cajas Totales (L+R)**",
        "• Suma de ambos lados.",
        "• **Típico:** 2-4 cajas (1-2 por lado).",
        "",
        "**Cuándo usar:**",
        "• Bandas grandes (> 8 músicos).",
        "• Cobertura general del escenario.",
        "• Complemento a wedges/IEMs.",
        "",
        "**8.3 IEMs (In-Ears) (Checkbox)**",
        "**F. Cantidad de Sistemas**",
        "• Número de sistemas IEM inalámbricos.",
        "• **Típico:** 5-10 (uno por músico principal).",
        "",
        "**G. Tipo**",
        "• **Stereo:** Mix estéreo (más inmersivo).",
        "• **Mono:** Mix mono (más sencillo, menor bandwidth RF)."
      ]
    },
    {
      id: "foh-control-drive",
      title: "9. CONTROL FOH & DRIVE",
      content: [
        "Define las especificaciones de tu sistema de control y procesamiento.",
        "**9.1 Consola FOH**",
        "**A. Cantidad**",
        "• **1 - Una sola consola (estándar)**",
        "• **2 - Setup redundante (backup console)**",
        "",
        "**B. Modelo de Consola**",
        "Categorías incluidas:",
        "**1. Prosumer / Small Format**",
        "• PreSonus StudioLive 64S",
        "• Behringer X32",
        "• Midas M32 LIVE",
        "**2. Rental / Professional Standard**",
        "• Allen & Heath dLive S7000",
        "• Yamaha CL5",
        "• DiGiCo SD12",
        "**3. High-End / Touring**",
        "• DiGiCo Quantum 338",
        "• Avid S6L-24C",
        "• Yamaha Rivage PM7",
        "",
        "**Para qué sirve:**",
        "• Documentación en el PDF (Technical Rider).",
        "• Cálculo de I/O requeridos.",
        "• Validación de outputs disponibles.",
        "",
        "**9.2 Drive Processor**",
        "El 'cerebro' del sistema PA. Matriz que recibe el audio de la consola y lo distribuye a los amplificadores.",
        "**Opciones:**",
        "• **Lake LM44/26:** Estándar pro audio.",
        "• **Meyer Galaxy 816:** Ecosistema Meyer.",
        "• **Outline Newton:** Tours Europa.",
        "• **Prodigy.MP (DirectAudio.MP)**",
        "• **Internal Console Matrix:** Matriz interna de la consola (sin procesador externo).",
        "",
        "**Para qué sirve:**",
        "• EQ global del sistema.",
        "• Delay management.",
        "• Protección de drivers.",
        "• Buffer de seguridad (consolas invitadas).",
        "",
        "**9.3 Modo de Operación**",
        "Define la arquitectura del sistema de control.",
        "**Opciones:**",
        "1. **Main Only (Solo FOH):** Una sola consola, todo el sistema. Uso: Shows pequeños, instalaciones.",
        "2. **Main + Backup (Redundante):** Dos consolas en espejo. Switcheo instantáneo si falla la principal. Uso: Shows críticos, broadcast.",
        "3. **FOH + Monitors (Separados):** Consola FOH + Consola Monitores independiente. Uso: Shows grandes, bandas exigentes.",
        "4. **Split A/B (Festivales):** FOH dividido en dos zonas (Stage A / Stage B). Uso: Festivales con cambios rápidos.",
        "5. **Broadcast Feed:** Feed dedicado para transmisión TV/streaming. Uso: Eventos televisados.",
        "",
        "**9.4 Transporte / Snake**",
        "Cómo viaja el audio desde FOH al escenario.",
        "**Opciones:**",
        "1. **Fibra Óptica (Optocore/Redundante):** Distancias ilimitadas (> 1km). Inmune a EMI/RFI. Redundancia Path A/B.",
        "2. **Digital CAT6 (Dante/AES50):** Ethernet estándar CAT6. Límite: 100m por segmento. Uso: Venues < 100m.",
        "3. **Analógico (Cobre Multipin):** Multicore cobre (24-48 canales). Pérdida HF en distancias > 50m. Uso: Venues pequeños, backup.",
        "4. **Híbrido (Digital + Analog Backup):** Digital primario + analógico de respaldo. Uso: Shows críticos con redundancia.",
        "",
        "**9.5 Infraestructura FOH**",
        "Campo de texto libre para describir la plataforma del FOH. Ejemplos:",
        "• 'Riser 4x3m, altura 1.2m'",
        "• 'Carpa 4x4m con techo'",
        "• 'EasyUp 3x3m'",
        "• 'FOH Integrado en grada'"
      ]
    },
    {
      id: "calibration-objectives",
      title: "10. OBJETIVOS DE CALIBRACIÓN",
      content: [
        "Este panel define las metas acústicas y de alineación temporal del sistema. Los objetivos establecen el \"blanco\" que el motor de cálculo intentará alcanzar.",
        "",
        "**10.1 Asistente de Objetivos (Presets Rápidos)**",
        "LiveSync Pro ofrece 3 presets automáticos basados en aplicaciones típicas:",
        "• **Corporativo:** 95dB @ 25m (conferencias, eventos de empresa, palabra hablada)",
        "• **Concierto:** 102dB @ 35m (conciertos acústicos, bandas en vivo, inteligibilidad alta)",
        "• **Festival:** 105dB @ 50m (festivales al aire libre, EDM, rock, cobertura extendida)",
        "",
        "Al hacer clic en cualquiera de estos botones, la aplicación ajustará automáticamente:",
        "• Target SPL",
        "• Distancia FOH",
        "• Distancia objetivo",
        "",
        "**10.2 Offset Físico Subs (m)**",
        "**Definición:** Distancia física (en metros) entre el PA principal (Tops) y los subwoofers.",
        "**Casos comunes:**",
        "• **0 metros:** Subs volados junto con Tops (flown configuration)",
        "• **2-5 metros:** Subs en el piso adelante del escenario (ground-stacked)",
        "• **Negativo:** Subs detrás del escenario (rare, delay compensation requerido)",
        "",
        "**Impacto:** Este parámetro es crítico para el cálculo de **alineación de fase** entre Sub y Main. LiveSync Pro calculará:",
        "• Desfase físico en grados",
        "• Delay recomendado para subs",
        "• Polaridad sugerida (normal o invertida)",
        "• Summation dB (nivel de acoplamiento)",
        "",
        "**10.3 Frecuencia de Crossover (Hz)**",
        "Frecuencia a la que el DSP divide la señal entre subwoofers y mains (típicamente 80-100 Hz).",
        "**Valores comunes:**",
        "• **80 Hz:** Configuración estándar (recomendada para la mayoría de los sistemas)",
        "• **90 Hz:** Subs con extensión superior (sistemas compactos)",
        "• **100 Hz:** Subs más grandes con respuesta de frecuencia extendida",
        "",
        "**10.4 Tipo de Filtro DSP**",
        "Especifica el tipo de filtro crossover del procesador:",
        "• **LR24 (Linkwitz-Riley 24dB/oct):** Estándar de la industria. Summation +6dB en fase. Desfase 360° total.",
        "• **BW24 (Butterworth 24dB/oct):** Más pronunciado, mayor group delay.",
        "• **BW12 (Butterworth 12dB/oct):** Pendiente suave, menor group delay, útil para sistemas compactos.",
        "",
        "**Nota técnica:** LiveSync Pro compensa automáticamente el **group delay** introducido por cada filtro, crítico para alineación de fase precisa.",
        "",
        "**10.5 SPL Objetivo (dB A-Weighted)**",
        "Nivel de presión sonora deseado en la posición FOH, medido como **LEQ (promedio temporal)**.",
        "**Valores de referencia:**",
        "• **85-90 dB:** Corporativo, conferencias, inteligibilidad crítica",
        "• **95-98 dB:** Conciertos acústicos, teatros",
        "• **100-105 dB:** Rock, pop, bandas en vivo",
        "• **105-110 dB+:** Festivales, EDM, eventos de alta energía",
        "",
        "**Importante:** Este valor NO es el SPL máximo (peaks), sino el promedio continuo durante un evento.",
        "",
        "**10.6 Distancia FOH (m)**",
        "Distancia en metros desde el PA principal hasta la consola FOH.",
        "**Regla general:**",
        "CODE:Distancia FOH = 1.5 × Ancho del Escenario",
        "**Por ejemplo:**",
        "• Escenario de 20m → FOH a 30m",
        "• Escenario de 24m → FOH a 36m",
        "",
        "**Impacto:** Esta distancia se usa para:",
        "• Calcular el SPL esperado en FOH",
        "• Verificar cobertura de inteligibilidad",
        "• Validar headroom disponible",
        "• Estimar pérdida atmosférica acumulada"
      ],
      subsections: [
        {
          title: "10.7 LIVESYNC PRO - SOUND METER (MANUAL DE USUARIO)",
          content: [
            "**1. Introducción**",
            "LiveSync Pro - Sound Meter es una herramienta de medición de precisión diseñada para ingenieros de sonido (FOH), técnicos de sistemas y diseñadores acústicos. La aplicación permite medir niveles de presión sonora (SPL) y niveles digitales (RMS/Peak) con precisión de laboratorio, permitiendo compensaciones por distancia y calibraciones personalizadas de hardware.",
            "",
            "**2. Configuración Inicial y Permisos**",
            "• **Permiso de Micrófono:** El navegador solicitará acceso al micrófono. Es obligatorio aceptarlo para que el motor de audio pueda procesar la señal.",
            "• **Modo de Navegador:** Se recomienda utilizar la aplicación en modo de pantalla completa o 'Añadir a pantalla de inicio' en dispositivos móviles para evitar que la barra de direcciones interfiera con la visibilidad.",
            "• **WakeLock:** La aplicación mantendrá automáticamente tu pantalla encendida mientras el motor de audio esté activo.",
            "",
            "**3. Interfaz Principal**",
            "**3.1 El Panel de Control (Action Bar):** Ubicado en la parte inferior de la pantalla, es el centro de mando.",
            "• **LED de Estado:** Un indicador cian pulsante confirma que el motor está procesando audio en tiempo real.",
            "• **Botón Start/Stop Engine:** Inicia o detiene el flujo de datos. Al detenerlo, se liberan los recursos del sistema y el micrófono.",
            "**3.2 Barra Lateral (Sidebar):**",
            "• **Live Sound Engine:** Acceso a la vista de análisis en tiempo real.",
            "• **System Calibration:** Herramientas para ajustar la ganancia digital y la sensibilidad física.",
            "• **Weighting Curve (Z/A/C):** Selección rápida de la curva de ponderación de frecuencia.",
            "• **Reset Session:** Borra todos los picos máximos y reinicia el acumulador Leq.",
            "",
            "**4. Live Sound Engine (Vista de Analizador)**",
            "**4.1 Medidor SPL Principal (Gauge):** Muestra el nivel de presión sonora actual. El anillo de color cambia de cian a amarillo y rojo según los umbrales de seguridad. Una marca blanca (Peak Needle) indica el pico máximo de los últimos 2 segundos.",
            "**4.2 Digital IO Metering (RMS & Peak):** Muestra los niveles internos en dBFS antes de la conversión a SPL. El 'Session Max' registra el valor más alto para detectar saturación (clipping).",
            "**4.3 Acoustic Summary (Métricas Calculadas):**",
            "• **Source Estimation:** Estima el SPL directamente en la fuente basándose en la distancia configurada.",
            "• **Leq (Integrated):** Nivel equivalente continuo (promedio de energía sonora).",
            "• **SPL Peak Hold:** Mantiene el valor SPL más alto registrado globalmente.",
            "",
            "**5. System Calibration (Vista de Configuración)**",
            "**5.1 Hardware Interface:** Permite seleccionar la entrada de audio (micrófono interno o interfaz USB externa).",
            "**5.2 Digital Trim & AES-17:** Ajustes finos de +/- 0.1dB y corrección AES-17 (+3dB) según el estándar.",
            "**5.3 Sens Offset (Calibración Física):** Ajuste más importante. Debe compararse con un medidor de referencia o calibrador acústico de 94dB.",
            "**5.4 Propagation Geometry (Compensación de Distancia):**",
            "• **Distancia:** Metros desde el micrófono hasta la fuente.",
            "• **Point Source:** Modelo de decaimiento esférico (-6dB por duplicación).",
            "• **Line Array:** Modelo de decaimiento cilíndrico (-3dB por duplicación).",
            "",
            "**6. Referencias Técnicas**",
            "**6.1 Curvas de Ponderación:**",
            "• **Z (Zero/Flat):** Sin filtros. Respuesta de frecuencia pura.",
            "• **A (A-Weighting):** Simula la respuesta del oído humano a niveles bajos. Estándar normativo.",
            "• **C (C-Weighting):** Para medir niveles de presión altos, conserva más información de bajos.",
            "**6.2 Respuesta Temporal:** FAST (125ms) para picos de presión; SLOW (1s) para lecturas estables."
          ]
        }
      ]
    },
    {
      id: "delay-towers-details",
      title: "11. TORRES DE DELAY",
      content: [
        "Las torres de delay son sistemas de refuerzo distribuido que extienden la cobertura del PA principal a zonas alejadas o con obstrucción visual.",
        "",
        "**11.1 Añadir Torre**",
        "Haz clic en el botón **\"+ AÑADIR FILA\"** para agregar una nueva torre al inventario. Puedes configurar múltiples torres independientes.",
        "",
        "**11.2 Parámetros de cada Torre**",
        "Cada torre tiene los siguientes campos configurables:",
        "",
        "**11.2.1 Nombre**",
        "Identificador único de la torre (ej: \"Torre Centro\", \"Delay 1 L/R\", \"Back Fill\", etc.). Este nombre aparecerá en los resultados de cálculo.",
        "",
        "**11.2.2 Distancia (m)**",
        "Distancia **horizontal** desde el PA principal hasta la torre, en metros.",
        "**Ejemplo:** Si el PA está en el escenario y la torre está a 60 metros detrás del FOH, ingresa **60m**.",
        "",
        "**11.2.3 Altura Relativa (m)**",
        "Diferencia de altura entre la torre de delay y el PA principal.",
        "**Casos:**",
        "• **0m:** Torre a la misma altura que el PA",
        "• **+2m:** Torre 2 metros más alta que el PA",
        "• **-3m:** Torre 3 metros más baja que el PA",
        "",
        "**Impacto:** LiveSync Pro calcula la **distancia de slant** (hipotenusa) considerando esta diferencia de altura usando el teorema de Pitágoras:",
        "CODE:Slant Distance = √(Distancia² + Altura²)",
        "Esta distancia slant es la que se usa para calcular el delay real, no solo la distancia horizontal.",
        "",
        "**11.2.4 Ancho L/R (m)**",
        "**Solo aplica en configuración Stereo.** Define la separación entre los arrays Left y Right de esta torre.",
        "**Valor típico:** 12-20 metros (depende del ancho del venue)",
        "**Nota:** Si tu configuración es Mono, este campo no afecta el cálculo.",
        "",
        "**11.2.5 Modelo**",
        "Modelo de altavoz para esta torre (selecciona del catálogo de 200+ modelos).",
        "**Importante:** Puedes usar el mismo modelo que el PA principal o un modelo diferente (por ejemplo, un line array más compacto para fill).",
        "",
        "**11.2.6 Cajas**",
        "Número de cajas (elementos) **por array** de la torre.",
        "**Ejemplo:** Si configuras 6 cajas y la torre es Stereo (L/R), tendrás **6 cajas Left + 6 cajas Right = 12 cajas totales**.",
        "",
        "**11.2.7 Cantidad**",
        "Número de filas/sets de esta torre.",
        "**Uso común:**",
        "• **1:** Una sola torre en configuración L/R",
        "• **2:** Dos torres independientes (ej: Left y Right separadas físicamente)",
        "• **4:** Array de 4 torres distribuidas (ej: configuración Arena 360°)",
        "",
        "**11.3 Cálculos Automáticos**",
        "Para cada torre, LiveSync Pro calcula:",
        "1. **Delay Time (ms):** Tiempo de retardo requerido para alinear temporalmente la torre con el PA principal, basado en:",
        "CODE:Delay = (Slant Distance / Speed of Sound) × 1000 ms",
        "2. **SPL en Objetivo:** Nivel de presión sonora que la torre produce en su zona objetivo.",
        "3. **Gain Trim (dB):** Ajuste de nivel sugerido para que la torre tenga **+3dB sobre el Main PA** en su zona de transición (Gain Shading).",
        "4. **Atmospheric Loss:** Pérdida por frecuencia según ISO 9613-1 (temperatura, humedad, altitud).",
        "5. **Headroom Warning:** Alerta si el sistema requiere más SPL del que físicamente puede producir.",
        "",
        "**11.4 Eliminar Torre**",
        "Haz clic en el ícono de **basura (🗑️)** en la esquina superior derecha de cada torre para eliminarla del inventario."
      ]
    },
    {
      id: "infra-network-safety",
      title: "12. INFRAESTRUCTURA, RED Y SEGURIDAD",
      content: [
        "Este panel consolida todos los aspectos técnicos de energía, rigging, electricidad y networking.",
        "",
        "**12.1 ENERGÍA Y FACTOR DE POTENCIA**",
        "**12.1.1 Voltaje de Amplificación (V)**",
        "Selecciona el voltaje de alimentación eléctrica disponible en el venue:",
        "**Opciones US / Japan:**",
        "• **110V:** Residencial (no recomendado para pro audio)",
        "• **120V:** Estándar US (outlets comunes)",
        "• **208V:** Pro Audio 3-Phase (ideal para racks de amplificadores)",
        "**Opciones Europa / LatAm:**",
        "• **220V:** Estándar LatAm",
        "• **230V:** Estándar Europeo",
        "• **240V:** UK/Australia",
        "",
        "**Impacto:** El voltaje afecta directamente:",
        "• Corriente requerida (I = P / V)",
        "• Calibre de cables necesarios",
        "• Pérdidas resistivas (I²R)",
        "• Damping Factor efectivo",
        "",
        "**12.1.2 Fases**",
        "Define si la instalación es monofásica o trifásica:",
        "• **Monofásica (Single Phase):** Todos los amplificadores en una sola fase (típico en eventos pequeños). Mayor corriente, mayor caída de voltaje.",
        "• **Trifásica (3-Phase):** Carga distribuida en 3 fases (L1, L2, L3). Recomendado para sistemas medianos/grandes. LiveSync Pro calculará el **balance de carga entre fases**.",
        "**Ventaja trifásica:** Reduce corriente por fase, mejora eficiencia, reduce caída de voltaje, mejora damping factor.",
        "",
        "**12.1.3 Marca de Amplificador**",
        "Selecciona la marca del amplificador que usarás. LiveSync Pro incluye un catálogo de amplificadores profesionales con datos reales de **Power Factor (PF)**.",
        "**Marcas disponibles:**",
        "• Lab.gruppen (PF: 0.98)",
        "• Powersoft (PF: 0.96-0.98)",
        "• d&b audiotechnik (PF: 0.99)",
        "• Crown, QSC, L-Acoustics, Meyer Sound, etc.",
        "**Si seleccionas \"Genérico\":** Se usa PF conservador de 0.70, lo cual sobrestima la corriente necesaria (enfoque seguro).",
        "",
        "**12.1.4 Modelo de Amplificador**",
        "Después de seleccionar la marca, elige el modelo específico. LiveSync Pro mostrará:",
        "• Potencia máxima (kW)",
        "• Power Factor real",
        "• Topología (Class D, Class AB, PFC Activo, etc.)",
        "**Ejemplo:** Lab.gruppen PLM 20000Q (20kW, PF=0.98)",
        "",
        "**12.1.5 Calibre Cable Altavoz (Gauge)**",
        "Define el grosor del cable que conecta los amplificadores a los altavoces (en estándar AWG):",
        "TABLE:Calibre AWG | Sección (mm²) | Uso Recomendado",
        "16 AWG | 1.31mm² | Muy fino, solo distancias cortas (<10m)",
        "14 AWG | 2.08mm² | Estándar corto (10-15m)",
        "12 AWG | 3.31mm² | Estándar Pro (15-30m) ✅ Recomendado",
        "10 AWG | 5.26mm² | Heavy Duty (30-50m)",
        "8 AWG | 8.36mm² | Subs / High Power (50-75m)",
        "4 AWG | 21.2mm² | Feeder / Extreme (>75m)",
        "",
        "**Impacto:** El calibre afecta la **resistencia del cable**, que a su vez afecta la caída de voltaje, el Damping Factor efectivo y la definición de graves (bass response).",
        "",
        "**12.1.6 Longitud Cable a Cajas (m)**",
        "Longitud total del recorrido del cable desde el amplificador hasta los altavoces (ida + vuelta).",
        "**Valores típicos:**",
        "• **5-10m:** Racks volados junto al PA (touring)",
        "• **20-30m:** Racks laterales o FOH",
        "• **50-100m:** Torres de delay lejanas",
        "⚠️ **Nota crítica:** Cables largos con calibre insuficiente pueden degradar severamente el damping factor, resultando en graves \"muddy\" (bajos indefinidos).",
        "",
        "**12.1.7 Tierra Física Verificada**",
        "⚠️ **CRÍTICO PARA SEGURIDAD:** Marca este checkbox **solo si verificaste físicamente** que el sistema tiene conexión a tierra adecuada.",
        "**Cómo verificar:**",
        "1. Usa un multímetro o probador de tierra.",
        "2. Mide resistencia entre ground pin y tierra real (<5 Ohms recomendado).",
        "3. Verifica continuidad en toda la cadena de distribución.",
        "**Si NO está verificada:** LiveSync Pro mostrará alertas de seguridad en el reporte logístico.",
        "",
        "**12.2 RED DIGITAL AUDIO**",
        "Esta sección calcula el **ancho de banda requerido** para redes Dante/AVB y valida si la infraestructura puede soportarlo.",
        "• **12.2.1 Protocolo:** Dante (estándar de facto) o AVB / Milan (IEEE 1722).",
        "• **12.2.2 Sample Rate:** 48 kHz (estándar) o 96 kHz (alta resolución). Nota: Mayor rate = mayor ancho de banda.",
        "• **12.2.3 Bit Depth:** 24-bit (estándar pro) o 32-bit Float (ultra resolución).",
        "• **12.2.4 Canales Totales:** Suma de Main L/R, Subs, Fills, Torres y Monitores.",
        "**Resultado:** LiveSync calculará los Mbps y determinará el estado: **\"100Mbps OK\"**, **\"1Gbps Required\"** o **\"Critical\"**.",
        "",
        "**12.3 RIGGING Y CARGA**",
        "**12.3.1 Capacidad Máx. por Punto (SWL)**",
        "Safe Working Load en kilogramos por punto de rigging. Este dato lo provee el venue, el rigger o el fabricante de los motores.",
        "TABLE:Entorno | Capacidad Típica (kg) | Tipo de Estructura",
        "Teatros Pequeños | 250 kg | Estructuras ligeras",
        "Eventos Medianos | 500 kg | Ground-supported roofs",
        "Arenas Pro | 1000 kg | Estructuras permanentes",
        "Estadios | 2000 kg | Acero reforzado",
        "",
        "**Alertas de Carga:**",
        "• 🟢 **Verde (Safe):** Carga <60% del SWL",
        "• 🟡 **Amarillo (Warning):** Carga 60-80% del SWL",
        "• 🔴 **Rojo (Critical):** Carga >80% del SWL → **NO VOLAR**",
        "",
        "**12.3.2 Motores por Array (Main)**",
        "• **1 Motor:** Array ligero (<150kg). Mayor stress en un solo punto.",
        "• **2 Motores:** Estándar para arrays medianos/grandes. Distribución de carga y mayor estabilidad. ✅ **Recomendado para line arrays profesionales**."
      ]
    },
    {
      id: "calculate-engine",
      title: "13. BOTÓN \"CALCULAR SISTEMA\"",
      content: [
        "Una vez completada toda la configuración, el botón **\"CALCULAR SISTEMA\"** (de color cyan brillante) ejecuta el motor de cálculo de LiveSync Pro.",
        "",
        "**13.1 ¿Qué hace este botón?**",
        "Al hacer clic, LiveSync Pro ejecuta más de **40 algoritmos científicos** en secuencia:",
        "",
        "**1. Física atmosférica:**",
        "• Velocidad del sonido (Cramer)",
        "• Presión barométrica (altitud)",
        "• Coeficiente de atenuación (ISO 9613-1)",
        "",
        "**2. Acústica de sala (si es Indoor):**",
        "• RT60 (Sabine o Eyring)",
        "• Modos axiales/tangenciales/oblicuos",
        "• Reflexiones early/late",
        "• Comb filtering",
        "",
        "**3. Line array physics:**",
        "• Coupling gain (acoplamiento mutuo)",
        "• Transición near/far field",
        "• Cobertura vertical",
        "• Penalidades off-axis",
        "",
        "**4. Alineación temporal:**",
        "• Delays para torres",
        "• Alineación de fase Sub/Main",
        "• Group delay de filtros crossover",
        "• Precedence effect (Haas)",
        "",
        "**5. Electricidad:**",
        "• Corriente total (monofásica o trifásica)",
        "• Balance de carga por fase",
        "• Damping factor efectivo",
        "• Caída de voltaje",
        "• Power compression",
        "",
        "**6. Rigging:**",
        "• Peso total por array",
        "• Carga por punto",
        "• Safety margin",
        "• Tensiones vectoriales (si bridle está habilitado)",
        "",
        "**7. Networking:**",
        "• Ancho de banda Dante/AVB",
        "• Validación de infraestructura",
        "",
        "**8. Logística:**",
        "• Bill of Materials (BOM)",
        "• Patch list (IO automatizado)",
        "• Requerimientos de potencia",
        "• Crew sizing"
      ],
      subsections: [
        {
          title: "13.2 Validaciones Pre-Cálculo",
          content: [
            "Antes de ejecutar el cálculo, LiveSync Pro valida:",
            "• Que los ángulos de splay coincidan con el número de cajas.",
            "• Que todos los campos requeridos estén completos.",
            "• Que los valores estén en rangos físicamente posibles.",
            "Si hay errores, aparecerá un mensaje describiendo qué falta corregir."
          ]
        },
        {
          title: "13.3 Tiempo de Cálculo",
          content: [
            "El cálculo toma típicamente **1-3 segundos** dependiendo de:",
            "• Complejidad del sistema (número de torres, fills, etc.).",
            "• Si es Indoor (cálculos de RT60/modos añaden complejidad).",
            "• Número de frecuencias analizadas (respuesta en frecuencia)."
          ]
        },
        {
          title: "13.4 ¿Qué sigue después?",
          content: [
            "Una vez completado el cálculo, la aplicación:",
            "1. Guarda los resultados en el proyecto actual.",
            "2. Te redirige automáticamente a la **vista de Resultados**.",
            "3. Muestra el **Reporte Técnico completo** con todas las métricas calculadas."
          ]
        }
      ]
    }
  ]
};
